import { all, call, put, takeLatest } from 'redux-saga/effects';
import { IAction } from 'redux/store/types';
import * as AppointmentService from 'services/appointment/appointment.service';
import * as ProxyService from 'services/proxy/proxy.service';
import {
  ACCEPT_APPOINTMENT,
  ACCEPT_APPOINTMENT_FAILED,
  REJECT_APPOINTMENT,
  REJECT_APPOINTMENT_PUBLIC,
  REJECT_APPOINTMENT_FAILED,
  CREATE_APPOINTMENT_REQUEST,
  CREATE_APPOINTMENT_REQUEST_FAILED,
  CREATE_APPOINTMENT_REQUEST_SUCCESSFUL,
  FETCH_APPOINTMENTS,
  FETCH_APPOINTMENTS_FAILED,
  FETCH_APPOINTMENTS_SUCCESSFUL,
  REJECT_APPOINTMENT_SUCCESSFUL,
  ACCEPT_APPOINTMENT_SUCCESSFUL,
  FETCH_ALL_PROVIDER_SERVICES,
  FETCH_ALL_PROVIDER_SERVICES_SUCCESSFUL,
  FETCH_MASTER_SCHEDULE,
  FETCH_MASTER_SCHEDULE_SUCCESSFUL,
  FETCH_INSURANCES_BY_STATE,
  FETCH_INSURANCES_BY_STATE_SUCCESSFUL,
  FETCH_INSURANCES_BY_STATE_FAILED,
  FETCH_ALL_PROVIDER_SERVICES_FAILED,
  RESCHEDULE_APPOINTMENT,
  RESCHEDULE_APPOINTMENT_PUBLIC,
  FETCH_RESCHEDULABLE_APPOINTMENTS,
  FETCH_RESCHEDULABLE_APPOINTMENTS_PUBLIC,
  FETCH_RESCHEDULABLE_APPOINTMENTS_SUCCESSFUL,
  FETCH_RESCHEDULABLE_APPOINTMENTS_FAILED,
  RESCHEDULE_NO_SHOW_APPOINTMENT,
  RESCHEDULE_NO_SHOW_APPOINTMENT_PUBLIC,
} from './actions';

import { showSnackbar } from 'redux/modules/snackbar';
import { AppointmentAction, AppointmentReschedule } from './types';
import { IGetAppointmentsQueryParams } from 'services/appointment/appointment.types';
import dayjs from '../../../utils/dayjs';
import CONFIG from 'config';

function* fetchAppointments(action: IAction) {
  try {
    const response = yield call(AppointmentService.getAppointments, action.payload.queryParams);
    if (response.status === 200) {
      const appointments = response?.data?.results;
      yield put({
        type: FETCH_APPOINTMENTS_SUCCESSFUL,
        payload: {
          appointments,
          currentPage: action.payload?.params?.pageNumber,
          totalPages: Math.ceil(response?.data?.total / response?.data?.size),
          totalRecords: response?.data?.total,
        },
      });
    } else {
      yield put({
        type: FETCH_APPOINTMENTS_FAILED,
        payload: {
          message: response.errors[0].endUserMessage,
        },
      });
    }
  } catch (e) {
    yield put({
      type: FETCH_APPOINTMENTS_FAILED,
      payload: { message: e },
    });
  }
}

function* createAppointment(action: IAction) {
  try {
    yield call(AppointmentService.requestNewAppointment, action.payload);
    yield put({ type: CREATE_APPOINTMENT_REQUEST_SUCCESSFUL });
    yield put(
      showSnackbar({
        snackType: 'success',
        snackMessage: 'Your appointment has created',
      })
    );
  } catch (e: any) {
    let message = '';
    if (e?.status === 500) {
      message = e?.data.error || 'Something went wrong';
    } else {
      message = e?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }

    yield put(
      showSnackbar({
        snackType: 'error',
        snackMessage: message,
      })
    );
    yield put({
      type: CREATE_APPOINTMENT_REQUEST_FAILED,
      payload: { message },
    });
  }
}

function* acceptAppointment(action: IAction) {
  const { appointmentId, requestBody } = action.payload;
  try {
    yield call(AppointmentService.acceptAppointment, requestBody, { appointmentId });
    yield put({
      type: ACCEPT_APPOINTMENT_SUCCESSFUL,
      payload: { appointmentId },
    });
    yield put(
      showSnackbar({
        snackType: 'success',
        snackMessage: 'Appointment request accepted',
      })
    );
  } catch (e: any) {
    let message = '';
    if (e?.status === 500) {
      message = e?.data.error || 'Something went wrong';
    } else {
      message = e?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }

    yield put(
      showSnackbar({
        snackType: 'error',
        snackMessage: message,
      })
    );
    yield put({
      type: ACCEPT_APPOINTMENT_FAILED,
      payload: { message },
    });
  }
}

function* rejectAppointment(action: AppointmentAction) {
  const { appointmentId, requestBody, onSuccess, isNoShow = false } = action.payload;
  try {
    yield call(AppointmentService.rejectAppointment, requestBody, { appointmentId });
    yield put({
      type: REJECT_APPOINTMENT_SUCCESSFUL,
      payload: { appointmentId, status: requestBody.action },
    });
    yield put(
      showSnackbar({
        snackType: 'success',
        snackMessage: isNoShow ? 'Appointment marked as no show.' : 'Appointment cancelled',
      })
    );
    if (onSuccess) {
      onSuccess();
    }
  } catch (e: any) {
    let message = '';
    if (e?.status === 500) {
      message = e?.data.error || 'Something went wrong';
    } else {
      message = e?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }

    yield put(
      showSnackbar({
        snackType: 'error',
        snackMessage: message,
      })
    );
    yield put({
      type: REJECT_APPOINTMENT_FAILED,
      payload: { message },
    });
  }
}

function* rejectAppointmentPublic(action: IAction) {
  const { requestBody, onSuccess, isNoShow = false } = action.payload;
  try {
    const { data } = yield call(ProxyService.bypassProxy, requestBody);
    yield put(
      showSnackbar({
        snackType: 'success',
        snackMessage: isNoShow ? 'Appointment marked as no show.' : 'Appointment cancelled',
      })
    );
    if (onSuccess) {
      onSuccess(data);
    }
  } catch (e: any) {
    let message = '';
    if (e?.status === 500) {
      message = e?.data.error || 'Something went wrong';
    } else {
      message = e?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }

    yield put(
      showSnackbar({
        snackType: 'error',
        snackMessage: message,
      })
    );
  }
}

function* rescheduleAppointment(action: AppointmentReschedule) {
  const { appointmentId, requestBody, onSuccess } = action.payload;
  try {
    const { data } = yield call(AppointmentService.rescheduleAppointment as any, requestBody, {
      appointmentId,
    });
    yield put(
      showSnackbar({
        snackType: 'success',
        snackMessage: 'Appointment rescheduled successfully.',
      })
    );
    if (onSuccess) {
      onSuccess(data);
    }
  } catch (e: any) {
    let message = '';
    if (e?.status === 500) {
      message = e?.data.error || 'Something went wrong';
    } else {
      message = e?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }

    yield put(
      showSnackbar({
        snackType: 'error',
        snackMessage: message,
      })
    );
  }
}

function* rescheduleAppointmentPublic(action: IAction) {
  const { requestBody, onSuccess } = action.payload;
  try {
    const { data } = yield call(ProxyService.bypassProxy, requestBody);
    yield put(
      showSnackbar({
        snackType: 'success',
        snackMessage: 'Appointment rescheduled successfully.',
      })
    );
    if (onSuccess) {
      onSuccess(data);
    }
  } catch (e: any) {
    let message = '';
    if (e?.status === 500) {
      message = e?.data.error || 'Something went wrong';
    } else {
      message = e?.data?.errors?.[0]?.endUserMessage || 'Something went wrong';
    }

    yield put(
      showSnackbar({
        snackType: 'error',
        snackMessage: message,
      })
    );
  }
}

function* fetchAllProviderServices(action) {
  try {
    const { initial, providerId, memberId, state } = action.payload;
    const params: any = {
      initial: !!initial,
    };
    if (state) {
      params.state = state;
    }
    if (providerId && providerId.trim() !== '') {
      params.providerId = providerId;
    }
    if (memberId && memberId.trim() !== '') {
      params.memberId = memberId;
    }
    const { data } = yield call(AppointmentService.getAllProviderServices, params);
    yield put({
      type: FETCH_ALL_PROVIDER_SERVICES_SUCCESSFUL,
      payload: {
        schedule: data,
      },
    });
  } catch (e: any) {
    console.warn(e);
    yield put({
      type: FETCH_ALL_PROVIDER_SERVICES_FAILED,
      payload: {
        error: e?.message,
      },
    });
  }
}

function* fetchMasterSchedule(action) {
  try {
    const { data } = yield call(AppointmentService.getAvailableSlots, action?.payload);
    yield put({
      type: FETCH_MASTER_SCHEDULE_SUCCESSFUL,
      payload: {
        data: data?.results.map(provider => {
          if (provider.slots?.length > 0) {
            provider.slots = provider.slots.map(slot => {
              return {
                ...slot,
                start: dayjs(slot.start).format('YYYY-MM-DDTHH:mm:ss'),
                end: dayjs(slot.end).format('YYYY-MM-DDTHH:mm:ss'),
              };
            });
          }
          return provider;
        }),
      },
    });
  } catch (e) {
    console.warn(e);
  }
}

function* fetchInsurance(action: IAction) {
  try {
    const { stateName } = action.payload;
    const response = yield call(AppointmentService.fetchInsurance, stateName);
    yield put({
      type: FETCH_INSURANCES_BY_STATE_SUCCESSFUL,
      payload: response.data,
    });
  } catch (e) {
    yield put({
      type: FETCH_INSURANCES_BY_STATE_FAILED,
      payload: { message: e },
    });
  }
}

/**
 * Reschedulable appointments are fetched in two bounded calls so the no-show date window
 * does not bleed onto the upcoming BOOKED query (the backend AND-s a single date range
 * across the whole request, on period.start):
 *  - BOOKED: all upcoming, no date bound.
 *  - NO_SHOW: the last 3 calendar days only (today + yesterday + the day 2 days ago) —
 *    the union of the admin today / yesterday / 48h-ago no-show pages.
 * Final eligibility (upcoming BOOKED / NO_SHOW, non-instant) and ordering live in the selector.
 */
export const BOOKED_QUERY_PARAMS: IGetAppointmentsQueryParams = {
  type: 'CURRENT',
  statuses: 'BOOKED',
  sortBy: 'startTime',
  orderBy: 'desc',
  page: 0,
  size: 100,
  searchQuery: '',
};

export const buildNoShowQueryParams = (): IGetAppointmentsQueryParams => ({
  type: 'PAST',
  statuses: 'NO_SHOW',
  startTime: dayjs().startOf('day').subtract(2, 'day').toISOString(),
  endTime: dayjs().endOf('day').toISOString(),
  sortBy: 'startTime',
  orderBy: 'desc',
  page: 0,
  size: 100,
  searchQuery: '',
});

const mergeResults = (booked: any, noShow: any) => [
  ...(booked?.data?.results || []),
  ...(noShow?.data?.results || []),
];

export function* fetchReschedulableAppointments() {
  try {
    const [booked, noShow] = yield all([
      call(AppointmentService.getAppointments, BOOKED_QUERY_PARAMS),
      call(AppointmentService.getAppointments, buildNoShowQueryParams()),
    ]);
    yield put({
      type: FETCH_RESCHEDULABLE_APPOINTMENTS_SUCCESSFUL,
      payload: { appointments: mergeResults(booked, noShow) },
    });
  } catch (e: any) {
    yield put({
      type: FETCH_RESCHEDULABLE_APPOINTMENTS_FAILED,
      payload: { message: e?.data?.errors?.[0]?.endUserMessage || 'Something went wrong' },
    });
  }
}

export function* fetchReschedulableAppointmentsPublic(action: IAction) {
  const { memberId } = action.payload;
  const searchUrl = `${CONFIG.api.baseUrl}/v2/scheduling/appointment/search`;
  try {
    const [booked, noShow] = yield all([
      call(ProxyService.bypassProxy, {
        url: searchUrl,
        method: 'GET',
        memberId,
        params: BOOKED_QUERY_PARAMS,
      }),
      call(ProxyService.bypassProxy, {
        url: searchUrl,
        method: 'GET',
        memberId,
        params: buildNoShowQueryParams(),
      }),
    ]);
    yield put({
      type: FETCH_RESCHEDULABLE_APPOINTMENTS_SUCCESSFUL,
      payload: { appointments: mergeResults(booked, noShow) },
    });
  } catch (e: any) {
    yield put({
      type: FETCH_RESCHEDULABLE_APPOINTMENTS_FAILED,
      payload: { message: e?.data?.errors?.[0]?.endUserMessage || 'Something went wrong' },
    });
  }
}

export function* rescheduleNoShowAppointment(action: IAction) {
  const { appointmentId, requestBody, onSuccess } = action.payload;
  try {
    const { data } = yield call(AppointmentService.rescheduleNoShowAppointment, requestBody, {
      appointmentId,
    });
    yield put(
      showSnackbar({
        snackType: 'success',
        snackMessage: 'Appointment rescheduled successfully.',
      })
    );
    if (onSuccess) {
      onSuccess(data);
    }
  } catch (e: any) {
    yield put(
      showSnackbar({
        snackType: 'error',
        snackMessage: e?.data?.errors?.[0]?.endUserMessage || e?.data?.error || 'Something went wrong',
      })
    );
  }
}

export function* rescheduleNoShowAppointmentPublic(action: IAction) {
  const { appointmentId, memberId, requestBody, onSuccess } = action.payload;
  try {
    const { data } = yield call(ProxyService.bypassProxy, {
      url: `${CONFIG.api.baseUrl}/v2/scheduling/appointment/rescheduleNoShow/${appointmentId}`,
      method: 'POST',
      memberId,
      body: requestBody,
    });
    yield put(
      showSnackbar({
        snackType: 'success',
        snackMessage: 'Appointment rescheduled successfully.',
      })
    );
    if (onSuccess) {
      onSuccess(data);
    }
  } catch (e: any) {
    yield put(
      showSnackbar({
        snackType: 'error',
        snackMessage: e?.data?.errors?.[0]?.endUserMessage || e?.data?.error || 'Something went wrong',
      })
    );
  }
}
export default function* appointmentSaga(): IterableIterator<any> {
  yield takeLatest(FETCH_APPOINTMENTS, fetchAppointments);
  yield takeLatest(CREATE_APPOINTMENT_REQUEST, createAppointment);
  yield takeLatest(ACCEPT_APPOINTMENT, acceptAppointment);
  yield takeLatest(REJECT_APPOINTMENT, rejectAppointment);
  yield takeLatest(REJECT_APPOINTMENT_PUBLIC, rejectAppointmentPublic);
  yield takeLatest(FETCH_ALL_PROVIDER_SERVICES, fetchAllProviderServices);
  yield takeLatest(FETCH_MASTER_SCHEDULE, fetchMasterSchedule);
  yield takeLatest(FETCH_INSURANCES_BY_STATE, fetchInsurance);
  yield takeLatest(RESCHEDULE_APPOINTMENT, rescheduleAppointment);
  yield takeLatest(RESCHEDULE_APPOINTMENT_PUBLIC, rescheduleAppointmentPublic);
  yield takeLatest(FETCH_RESCHEDULABLE_APPOINTMENTS, fetchReschedulableAppointments);
  yield takeLatest(FETCH_RESCHEDULABLE_APPOINTMENTS_PUBLIC, fetchReschedulableAppointmentsPublic);
  yield takeLatest(RESCHEDULE_NO_SHOW_APPOINTMENT, rescheduleNoShowAppointment);
  yield takeLatest(RESCHEDULE_NO_SHOW_APPOINTMENT_PUBLIC, rescheduleNoShowAppointmentPublic);
}
